import React from 'react'

import { faCheck, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { faCircleCheck, faCircleXmark } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import { Progress as AntProgress } from 'antd'

import { ACCENT_COLORS } from '../../constants/colors'

import {
  PROGRESS_BAR_HEIGHT,
  PROGRESS_CIRCLE_DIAMETER,
  PROGRESS_STATE_ACCENT,
  PROGRESS_STATUS_MAP
} from './constants'
import { useStyles } from './styles'
import type { ProgressProps } from './types'

export const Progress: React.FC<ProgressProps> = (props) => {
  const { state = 'default', percent, className } = props
  const color = ACCENT_COLORS[PROGRESS_STATE_ACCENT[state]]
  const status = PROGRESS_STATUS_MAP[state]
  const displayPercent = Math.round(percent)
  const { styles, cx } = useStyles({ color })

  if (props.variant === 'circle') {
    const { size = 'large', showValue = true } = props
    const diameter = PROGRESS_CIRCLE_DIAMETER[size]
    const isDefaultState = state === 'default'
    const showInfo = !isDefaultState || showValue

    // Always pass a custom `format` (instead of relying on antd's built-in
    // success/exception icons) so the center renders the design system's
    // FontAwesome icons rather than `@ant-design/icons`.
    const format = () => {
      if (state === 'success') {
        return (
          <FontAwesomeIcon
            icon={faCheck}
            className={styles.circleIcon}
            aria-hidden
          />
        )
      }
      if (state === 'error') {
        return (
          <FontAwesomeIcon
            icon={faXmark}
            className={styles.circleIcon}
            aria-hidden
          />
        )
      }
      return (
        <span
          className={cx(
            styles.circleValue,
            size === 'small' && styles.circleValueSmall
          )}
        >
          {displayPercent}%
        </span>
      )
    }

    return (
      <AntProgress
        type="circle"
        percent={percent}
        size={diameter}
        status={status}
        strokeColor={color}
        showInfo={showInfo}
        format={format}
        className={cx(styles.circle, className)}
      />
    )
  }

  const { size = 'large', showValue = true, showIcon = true } = props
  const height = PROGRESS_BAR_HEIGHT[size]
  const showStatusIcon = showIcon && state !== 'default'
  const showInfo = showValue || showStatusIcon

  const format = () => (
    <span
      className={cx(styles.barInfo, size === 'small' && styles.barInfoSmall)}
    >
      {showValue && <span>{displayPercent}%</span>}
      {showStatusIcon && (
        <FontAwesomeIcon
          icon={state === 'success' ? faCircleCheck : faCircleXmark}
          className={styles.barIcon}
          aria-hidden
        />
      )}
    </span>
  )

  return (
    <AntProgress
      type="line"
      percent={percent}
      size={{ height }}
      status={status}
      strokeColor={color}
      showInfo={showInfo}
      format={format}
      className={cx(
        size === 'small' ? styles.barGapSmall : styles.barGapDefault,
        className
      )}
    />
  )
}
